/**
 * Coaching hints static fixture — EPIC-06-02.
 *
 * Provides per-scenario coaching hints displayed in the in-session hints panel.
 * Keyed by scenario_id (UUID from ScenarioService._SEED_SCENARIOS).
 *
 * ## Adding hints for a new scenario
 * 1. Add a new entry to COACHING_HINTS below, keyed by the scenario's UUID.
 * 2. Fill all three required fields:
 *    - `aboutPersona`   — 1–2 sentences about the AI customer's goals/style.
 *    - `scoringFocus`   — exactly 4 items, one per competency.
 *    - `commonMistakes` — 3 bullet points for the most frequent agent errors.
 *
 * ## Future
 * Replace with a `GET /api/v1/scenarios/{id}/hints` API call so admins can
 * update hints without a frontend deploy (tracked in EPIC-06-02 story notes).
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ScoringFocusItem {
  /** One of: communication | product_knowledge | compliance | objection_handling */
  competency: string;
  /** One-sentence coaching tip for this competency. */
  tip: string;
}

export interface CoachingHints {
  /** 1–2 sentence description of the AI customer persona's goals and style. */
  aboutPersona: string;
  /** Exactly four items — one per scored competency. */
  scoringFocus: ScoringFocusItem[];
  /** Three common agent errors for this scenario type. */
  commonMistakes: string[];
}

/** Keyed by scenario UUID (matches ScenarioService._SEED_SCENARIOS ids). */
export type CoachingHintsMap = Record<string, CoachingHints>;

// ---------------------------------------------------------------------------
// Static fixture data
// ---------------------------------------------------------------------------

/**
 * Scenario: "Life Insurance Introduction" (zh-TW)
 * ID: aab4e3d0-1234-5678-abcd-112233445566
 */
const LIFE_INTRO_ZH: CoachingHints = {
  aboutPersona:
    'This customer is purchasing life insurance for the first time and is unfamiliar with '
    + 'policy terms. They are open to advice but confused by jargon — clear, simple language '
    + 'will build trust quickly.',
  scoringFocus: [
    {
      competency: 'communication',
      tip: 'Speak at a steady pace, confirm understanding after each key point, '
        + 'and avoid abbreviations like "CV" or "SV" without explanation.',
    },
    {
      competency: 'product_knowledge',
      tip: 'Explain the death benefit, premium payment period, and cash value in plain terms. '
        + 'Be ready to compare term vs. whole-life if asked.',
    },
    {
      competency: 'compliance',
      tip: 'Disclose all fees, the free-look period, and surrender charges before closing. '
        + 'Never guarantee investment returns.',
    },
    {
      competency: 'objection_handling',
      tip: 'When the customer says the premium is too high, first acknowledge the concern, '
        + 'then offer to adjust the coverage amount or payment term rather than discounting.',
    },
  ],
  commonMistakes: [
    'Using insurance jargon (e.g. "IRR", "CSV") without first explaining what it means.',
    'Jumping to product recommendations before completing a proper needs analysis.',
    'Forgetting to explain the surrender value and what happens if the policy is cancelled early.',
  ],
};

/**
 * Scenario: "Health Insurance Upsell" (zh-TW)
 * ID: bbc5e3d0-2345-6789-bcde-223344556677
 */
const HEALTH_UPSELL_ZH: CoachingHints = {
  aboutPersona:
    'This customer already holds a life insurance policy with Allianz and is now being '
    + 'approached about adding a health or critical illness rider. They may resist because '
    + 'they believe their existing coverage is "enough."',
  scoringFocus: [
    {
      competency: 'communication',
      tip: 'Reference the customer\'s existing policy by name — this shows you know their '
        + 'situation and builds credibility.',
    },
    {
      competency: 'product_knowledge',
      tip: 'Clearly explain the difference between the life insurance death benefit and the '
        + 'health rider\'s living-benefit structure (pays on diagnosis, not death).',
    },
    {
      competency: 'compliance',
      tip: 'State clearly that adding a rider will increase the monthly premium and obtain '
        + 'verbal confirmation before proceeding.',
    },
    {
      competency: 'objection_handling',
      tip: 'When the customer says "I already have coverage," agree — then explain the gap: '
        + 'life insurance pays beneficiaries after death; health riders pay the insured while alive.',
    },
  ],
  commonMistakes: [
    'Not linking the new rider to the customer\'s existing policy — makes the pitch feel generic.',
    'Overselling by listing every rider feature without tying each one to the customer\'s stated concerns.',
    'Failing to disclose the premium increase before asking the customer to commit.',
  ],
};

/**
 * Scenario: "Life Insurance Introduction (EN)"
 * ID: ccd6f4e1-3456-7890-cdef-334455667788
 */
const LIFE_INTRO_EN: CoachingHints = {
  aboutPersona:
    'This is an English-speaking expatriate unfamiliar with Taiwan\'s insurance regulations. '
    + 'They may have coverage from their home country and wonder if local insurance is necessary. '
    + 'Keep explanations factual and cross-border friendly.',
  scoringFocus: [
    {
      competency: 'communication',
      tip: 'Use clear, formal English. Avoid Mandarin insurance terminology even in passing. '
        + 'Confirm residency status early — it affects policy eligibility.',
    },
    {
      competency: 'product_knowledge',
      tip: 'Be prepared to compare Taiwan local coverage with international plans. '
        + 'Know which benefits apply to non-citizens and any residency-period requirements.',
    },
    {
      competency: 'compliance',
      tip: 'Explain that Taiwan NHI (National Health Insurance) only partially covers '
        + 'expatriates — this is the primary compliance gap to address honestly.',
    },
    {
      competency: 'objection_handling',
      tip: 'If the customer says "I have coverage from home," validate it, then explain '
        + 'the local reimbursement process and how a local plan removes the claims delay.',
    },
  ],
  commonMistakes: [
    'Assuming the customer understands Taiwan-specific regulations without explaining them.',
    'Failing to ask about existing home-country coverage before recommending a plan.',
    'Not addressing the residency requirement for long-term policies.',
  ],
};

/**
 * Default hints — shown when no scenario-specific hints are available.
 * Used as fallback for scenario IDs not listed above.
 */
export const DEFAULT_COACHING_HINTS: CoachingHints = {
  aboutPersona:
    'This customer is a prospective Allianz client. Listen carefully to their situation '
    + 'before recommending any product.',
  scoringFocus: [
    {
      competency: 'communication',
      tip: 'Speak clearly, listen actively, and confirm understanding with open questions.',
    },
    {
      competency: 'product_knowledge',
      tip: 'Demonstrate accurate knowledge of the product\'s core features and limitations.',
    },
    {
      competency: 'compliance',
      tip: 'Follow the Allianz sales compliance checklist: disclose fees, free-look, and surrender charges.',
    },
    {
      competency: 'objection_handling',
      tip: 'Acknowledge every objection before responding — never talk over the customer.',
    },
  ],
  commonMistakes: [
    'Skipping the needs-analysis and jumping straight to product features.',
    'Making return guarantees or comparing to investment products.',
    'Closing before the customer has confirmed they understand the key terms.',
  ],
};

/**
 * All scenario hints, keyed by scenario UUID.
 * Falls back to DEFAULT_COACHING_HINTS for unknown scenario IDs.
 */
export const COACHING_HINTS: CoachingHintsMap = {
  'aab4e3d0-1234-5678-abcd-112233445566': LIFE_INTRO_ZH,
  'bbc5e3d0-2345-6789-bcde-223344556677': HEALTH_UPSELL_ZH,
  'ccd6f4e1-3456-7890-cdef-334455667788': LIFE_INTRO_EN,
};

/**
 * Look up coaching hints for a scenario, falling back to the default.
 *
 * @param scenarioId - UUID of the scenario (from sessionStorage 'scenario_id').
 */
export function getCoachingHints(scenarioId: string | null | undefined): CoachingHints {
  if (!scenarioId) return DEFAULT_COACHING_HINTS;
  return COACHING_HINTS[scenarioId] ?? DEFAULT_COACHING_HINTS;
}
